// Mob catalog for Nexus Forge expeditions (Phase 5 Step 8).
//
// Data-driven: adding a mob is a config change here. Precedent is
// shared/abilities.ts (Step 6) and shared/recipes.ts — frozen entries,
// a catalog map + an ordered list, and a `getMob` lookup helper.
//
// Phase 5 ships three types: two non-boss (drone / scav) and one boss
// (derelict-boss). The non-boss pair gives the Derelict Ship archetype
// a ranged+melee mix; the boss carries a forward-declared
// `uniqueAbilityId` that Step 9 wires into a behavior — Step 8 only
// carries the field as catalog data.
//
// Mobs do NOT consume shared/abilities.ts. That catalog is player-only;
// mob attack stats (damage, range, cooldown) live here so the two
// systems stay cleanly separated. A mob tick resolves hits from these
// fields directly.
//
// Movement step sizes (`chaseStepPx`, `wanderStepPx`) are carried on the
// MobDef beyond the Step 8 brief's pre-locked field list. See
// DECISIONS 2026-04-19 "[Phase 5, Step 8] MobDef carries chaseStepPx
// and wanderStepPx beyond the brief" — the per-line contract check
// forbids implementation-file literals, and balance-combat.ts doesn't
// exist yet in the repo, so catalog-local fields keep movement
// data-driven without plumbing through a non-existent balance surface.

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

export const MOB_TYPES = ["drone", "scav", "derelict-boss"] as const;
export type MobType = (typeof MOB_TYPES)[number];

// Role is advisory metadata: 'ranged' mobs prefer to strike from distance
// (higher attackRange), 'melee' mobs close the gap. Step 9 may specialize
// hit-resolution or aggro behavior per role; Step 8 just carries the tag.
export type MobRole = "melee" | "ranged";

export type MobDef = {
  readonly id: MobType;
  readonly name: string;
  readonly maxHp: number;
  // Mean damage per hit. `damage ± damageVariance` uniform jitter, same
  // shape as AbilityDef.damageBase / damageVariance so Step 9's hit
  // resolver can share a single code path for player and mob strikes.
  readonly damage: number;
  readonly damageVariance: number;
  // Squared distance cap at which the mob can deliver an attack.
  readonly attackRange: number;
  // Distance at which the mob acquires a target and transitions IDLE→AGGRO.
  readonly aggroRange: number;
  // Per-mob tick cadence in ms. Step 9's ExpeditionDO alarm loop reads
  // this; Step 8 only carries the datum.
  readonly tickMs: number;
  readonly role: MobRole;
  readonly isBoss: boolean;
  // Forward-declared ability id — Step 9 wires the behavior. For Phase 5
  // only the boss carries one ('boss-cone-slam').
  readonly uniqueAbilityId?: string;
  // Pixels advanced toward the aggro target per tick while in AGGRO.
  readonly chaseStepPx: number;
  // Bipolar jitter magnitude for IDLE wander. Positions jitter within
  // [-wanderStepPx, +wanderStepPx] per tick (triangular distribution).
  readonly wanderStepPx: number;
};

// -----------------------------------------------------------------------
// Catalog entries — values pre-locked by reviewer (Step 8 brief).
// Every numeric literal below is a data-catalog value per the same
// reasoning as shared/abilities.ts and shared/recipes.ts: cooldowns,
// damage, range, tick cadence, and per-mob movement pace do not belong
// in balance.ts (not cross-entity tuning knobs).
// -----------------------------------------------------------------------

const drone: MobDef = {
  id: "drone",
  name: "Recon Drone",
  maxHp: 30, // contracts:allow-literal — mob catalog datum
  damage: 3, // contracts:allow-literal — mob catalog datum
  damageVariance: 1,
  attackRange: 200, // contracts:allow-literal — mob catalog datum
  aggroRange: 150, // contracts:allow-literal — mob catalog datum
  tickMs: 500, // contracts:allow-literal — mob catalog datum
  role: "ranged",
  isBoss: false,
  chaseStepPx: 12, // contracts:allow-literal — mob catalog datum
  wanderStepPx: 4, // contracts:allow-literal — mob catalog datum
};

const scav: MobDef = {
  id: "scav",
  name: "Derelict Scavenger",
  maxHp: 30, // contracts:allow-literal — mob catalog datum
  damage: 4, // contracts:allow-literal — mob catalog datum
  damageVariance: 1,
  attackRange: 50, // contracts:allow-literal — mob catalog datum
  aggroRange: 100, // contracts:allow-literal — mob catalog datum
  tickMs: 800, // contracts:allow-literal — mob catalog datum
  role: "melee",
  isBoss: false,
  chaseStepPx: 18, // contracts:allow-literal — mob catalog datum
  wanderStepPx: 3, // contracts:allow-literal — mob catalog datum
};

const derelictBoss: MobDef = {
  id: "derelict-boss",
  name: "Derelict Overseer",
  maxHp: 300, // contracts:allow-literal — mob catalog datum
  damage: 10, // contracts:allow-literal — mob catalog datum
  damageVariance: 3, // contracts:allow-literal — mob catalog datum
  attackRange: 80, // contracts:allow-literal — mob catalog datum
  aggroRange: 200, // contracts:allow-literal — mob catalog datum
  tickMs: 500, // contracts:allow-literal — mob catalog datum
  role: "melee",
  isBoss: true,
  uniqueAbilityId: "boss-cone-slam",
  chaseStepPx: 20, // contracts:allow-literal — mob catalog datum
  wanderStepPx: 2, // contracts:allow-literal — mob catalog datum
};

// -----------------------------------------------------------------------
// Catalog map + ordered list — mirrors abilities.ts / recipes.ts shape.
// -----------------------------------------------------------------------

export const MOB_CATALOG: Readonly<Record<MobType, MobDef>> = {
  drone,
  scav,
  "derelict-boss": derelictBoss,
};

export const mobList: readonly MobDef[] = [drone, scav, derelictBoss];

export function getMob(id: string): MobDef | undefined {
  return (MOB_CATALOG as Readonly<Record<string, MobDef>>)[id];
}

export function isMobType(id: string): id is MobType {
  return id in MOB_CATALOG;
}
